import React from 'react';

/**
 * Status badges — reusable, colour-coded pills for Order Status and
 * Payment Status. Colours follow the customer's exact specification:
 *
 *   Order Status   Placed=gray | Confirmed=blue | Dispatched=orange |
 *                  Delivered=green | Cancelled=red
 *   Payment Status Pending=yellow | Paid=green
 *
 * Legacy Order Status values (packed / shipped) are folded into
 * Dispatched's colour so any order that hasn't yet gone through the
 * super-admin timeline backfill still renders sensibly.
 *
 * Legacy Payment Status values (cod_pending / credit_pending) are treated
 * as `pending` for display, matching the payment-status backfill contract.
 * The label always shows the canonical two-state value.
 *
 * Both components render a compact rounded pill using the app's existing
 * typography scale (tracking-widest uppercase 10px) so they slot cleanly
 * next to text or inside tables.
 */

const ORDER_STATUS_STYLES = {
    placed:     'bg-neutral-100 text-neutral-700 border-neutral-300',
    confirmed:  'bg-blue-100    text-blue-800    border-blue-300',
    dispatched: 'bg-orange-100  text-orange-800  border-orange-300',
    packed:     'bg-orange-100  text-orange-800  border-orange-300', // legacy
    shipped:    'bg-orange-100  text-orange-800  border-orange-300', // legacy
    delivered:  'bg-emerald-100 text-emerald-800 border-emerald-300',
    cancelled:  'bg-red-100     text-red-800     border-red-300',
};

const PAYMENT_STATUS_STYLES = {
    pending: 'bg-amber-100   text-amber-900    border-amber-300',
    paid:    'bg-emerald-100 text-emerald-800  border-emerald-300',
};

export function normalizePaymentStatus(raw) {
    const s = String(raw || '').toLowerCase();
    if (s === 'paid') return 'paid';
    // Anything else (pending, cod_pending, credit_pending, empty, unknown)
    // is displayed as `pending` — the two-state model.
    return 'pending';
}

/**
 * True when a Credit order has been dispatched (or delivered) but the
 * distributor has not yet paid. Used across admin tables to render a
 * soft amber highlight so employees can chase overdue collections.
 *
 * We deliberately exclude COD orders (they're supposed to be settled on
 * delivery and don't have the same "trust window" as credit purchases)
 * and cancelled orders (nothing to collect).
 */
export function isOverdueCollection(order) {
    if (!order) return false;
    if (order.payment_method !== 'credit') return false;
    if (normalizePaymentStatus(order.payment_status) === 'paid') return false;
    const st = String(order.status || '').toLowerCase();
    return st === 'dispatched' || st === 'delivered';
}

/**
 * How many whole days have elapsed since credit was effectively extended.
 *
 * Anchor priority:
 *   1. `timeline[status='dispatched'].at` — clock starts the moment goods
 *      leave the warehouse (most accurate).
 *   2. `timeline[status='delivered'].at` — fallback for orders that jumped
 *      straight to delivered without a recorded dispatched step.
 *   3. `created_at` — final safety net for legacy orders whose timeline
 *      array is missing (should be rare after the state-machine rollout).
 *
 * Returns:
 *   • `null` when the order is not overdue (COD, cancelled, paid, or
 *     still in placed/confirmed).
 *   • a non-negative integer otherwise. Same-day / future-timestamped
 *     rows return 0 rather than a negative number so the UI can render
 *     "Just now" without special-casing negatives.
 */
export function overdueDays(order) {
    if (!isOverdueCollection(order)) return null;
    let anchor = null;
    const tl = Array.isArray(order?.timeline) ? order.timeline : [];
    for (const entry of tl) {
        if (entry?.status === 'dispatched' && entry?.at) { anchor = entry.at; break; }
    }
    if (!anchor) {
        for (const entry of tl) {
            if (entry?.status === 'delivered' && entry?.at) { anchor = entry.at; break; }
        }
    }
    if (!anchor) anchor = order?.created_at;
    if (!anchor) return null;
    const then = new Date(anchor).getTime();
    if (Number.isNaN(then)) return null;
    const diffMs = Date.now() - then;
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / 86_400_000);
}

/**
 * Compact human label for the overdue-age of a single order.
 *   0  → "Today"
 *   1  → "1 day"
 *   N  → "N days"
 * Callers concatenate this into their own copy (e.g. "5 days overdue").
 */
export function formatOverdueAge(order) {
    const d = overdueDays(order);
    if (d === null) return null;
    if (d === 0) return 'Today';
    if (d === 1) return '1 day';
    return `${d} days`;
}

export const OrderStatusBadge = ({ status, className = '', testid }) => {
    const key = String(status || '').toLowerCase();
    const cls = ORDER_STATUS_STYLES[key] || 'bg-neutral-100 text-neutral-700 border-neutral-300';
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border whitespace-nowrap ${cls} ${className}`}
            data-testid={testid || 'order-status-badge'}
        >
            {key || 'unknown'}
        </span>
    );
};

export const PaymentStatusBadge = ({ status, className = '', testid }) => {
    const key = normalizePaymentStatus(status);
    const cls = PAYMENT_STATUS_STYLES[key];
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border whitespace-nowrap ${cls} ${className}`}
            data-testid={testid || 'payment-status-badge'}
        >
            {key}
        </span>
    );
};
